import ReactECharts from 'echarts-for-react';
import _ from "lodash";
import PropTypes from "prop-types";
import React from "react";
import { newShade } from '../../utilities/helpers.js';
import { getYAxisIndex } from './Helpers.js';

const LineChart = (props) => {
  const {
    areaChart = false,
    chartData = [],
    chartheight,
    CloudCostSummaryChart = false,
    color = [],
    data = [],
    events = {},
    isScroll = false,
    isScrollEndLimit = 30,
    isScrollStartLimit = 0,
    legend = [],
    onChartClick,
    otherChartStyle,
    scrollBarBottomAlign = 15,
    shadeAreaDeep = false,
    showlegend = true,
    sirartrend = false,
    smooth = false,
    spaceBottom = "15%",
    spaceLeft = "3%",
    spaceRight = "5%",
    spaceTop = "10%",
    symboleSize = 2,
    toolTipConfig,
    xAxisConfig = {},
    XaxisLabelFormat = {},
    xAxisData = [],
    xField = "",
    xnamegap = 32,
    yAxisConfig = {},
    yField = "",
    symbolSize,
    legendPosition,
    isNoGradient = false,
    YaxisLableConfig,
    customizeLine,
    legendCustomFormatter = {
      formatter: (params) => {
        return params?.trim();
      },
    },
    customSeries,
  } = props;

  let maxValue = Number.NEGATIVE_INFINITY;

  for (const row of data) {
    const maxInRow = Math.max(...row);
    if (maxInRow > maxValue) {
      maxValue = maxInRow;
    }
  }

  const formatYAxis = (value) => {
    //y axis data calcu
    const abs = Math.abs(value); //abs refers to absolute value

    if (abs >= 1_000_000_000_000) {
      return `${(value / 1_000_000_000_000).toFixed(1).replace(/\.0$/, "")}T`;
    }
    if (abs >= 1_000_000_000) {
      return `${(value / 1_000_000_000).toFixed(1).replace(/\.0$/, "")}B`;
    }
    if (abs >= 1_000_000) {
      return `${(value / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
    }
    if (abs >= 1_000) {
      return `${(value / 1_000).toFixed(1).replace(/\.0$/, "")}K`;
    }
    return Math.round(value).toString();
  };

  // Flatten the two-dimensional array
  const flattenedData = data.flat();
  const formattedValues = flattenedData.map((value) =>
    formatYAxis(Math.floor(value))
  );

  const maxDigits = Math.max(
    ...formattedValues?.map((value) => value.toString().length)
  );

  const nameGapYAxis = (maxDigits) => {
    let nameGap = 29; // Default value for 2-digit numbers
    if (maxDigits === 3) {
      nameGap = 40;
    } else if (maxDigits === 4) {
      nameGap = 45;
    } else if (maxDigits === 5) {
      nameGap = 48;
    } else if (maxDigits === 6) {
      nameGap = 50;
    } else if (maxDigits === 7) {
      nameGap = 52;
    }
    return nameGap;
  };

  const recalcNameGap = () => {
    if (!chartInstance) {
      return;
    }

    const yAxisModel = chartInstance.getModel().getComponent("yAxis");
    if (!yAxisModel) {
      return;
    }

    const axis = yAxisModel.axis;
    const tickValues = axis.scale.getTicks();

    // Extract and format values
    const renderedYAxisLabels = tickValues.map((tick) => {
      const numValue = Number(tick.value);
      return isNaN(numValue) ? "0" : formatYAxis(numValue);
    });

    // Find the longest label length
    const longestLabelLength = Math.max(
      ...renderedYAxisLabels.map((label) => label.length),
      2
    );

    chartInstance.setOption({
      yAxis: {
        nameGap: nameGapYAxis(longestLabelLength),
      },
    });
  };

  const handleChartReady = (chart) => {
    chartInstance = chart;
    recalcNameGap();
  };

  /// line value mark item custom
  const customizeedLineitemStyle = (seriesColor) => {
    return {
      itemStyle: {
        color: "#fff",
        borderColor: seriesColor,
        borderWidth: 2,
      },
      lineStyle: {
        color: seriesColor,
      },
    };
  };

  ////
  const getSeriesData = () => {
    let seriesData = [];

    if (CloudCostSummaryChart) {
      seriesData?.push(
        {
          type: "line",
          data: chartData[0]?.value,
          symbolSize: symboleSize,
          areaStyle: null,
          emphasis: {
            disabled: true,
          },
        },
        {
          type: "line",
          data: chartData[1]?.value,
          symbolSize: symboleSize || 2,
          lineStyle: {
            color: "orange",
          },
          areaStyle: null,
          emphasis: {
            disabled: true,
          },
        }
      );
    } else if (customSeries) {
      seriesData = customSeries;
    } else {
      if (data.length > 0) {
        const firstObjectKeys = Object.keys(data[0]);
        firstObjectKeys?.forEach((key, outerIndex) => {
          const seriesColor = color ? color[outerIndex] : "";
          const opacity = otherChartStyle?.opacity
            ? otherChartStyle?.opacity[outerIndex]
            : 100;
          const customizedStyles = customizeLine
            ? customizeedLineitemStyle(seriesColor)
            : {};
          seriesData?.push({
            name: legend[outerIndex],
            type: "line",
            smooth: smooth && true,
            data: data?.map((item) => item[key]),
            symbolSize: symbolSize ? symbolSize : 7, // Slightly larger symbol for better visibility
            symbol: "circle",
            itemStyle: customizeLine
              ? customizedStyles.itemStyle
              : {
                  color: seriesColor,
                  borderColor: "#fff",
                  borderWidth: 1,
                },
            lineStyle: customizeLine
              ? customizedStyles.lineStyle
              : otherChartStyle?.lineStyle?.lineStyle || {},
            areaStyle:
              areaChart && seriesColor
                ? {
                    opacity: opacity,
                    color: isNoGradient
                      ? seriesColor //  solid fill if `isNoGradient` is true
                      : {
                          colorStops: [
                            {
                              offset: 0,
                              color: seriesColor,
                            },
                            {
                              offset: 1,
                              color: newShade(seriesColor, 250),
                            },
                          ],
                          x: 0,
                          y: 0,
                          x2: 0,
                          y2: 1,
                          global: false,
                        },
                  }
                : null,
          });
        });
      }
    }

    if (shadeAreaDeep && color) {
      seriesData?.forEach((series) => {
        const seriesColor = series?.itemStyle ? series?.itemStyle?.color : "";
        if (series?.areaStyle && series?.areaStyle?.color) {
          sirartrend
            ? (series.areaStyle.color = {
                type: "linear",
                x: 0,
                y: 0,
                x2: 0,
                y2: 0.7,
                colorStops: [
                  {
                    offset: 0,
                    color: newShade(seriesColor, 50),
                  },
                  {
                    offset: 1,
                    color: "#FFFFFF",
                  },
                  {
                    offset: 0.7, // Line-focused color
                    color: "#FFFFFF",
                  },
                  {
                    offset: 1, // Fades into white away from the line
                    color: "#FFFFFF",
                  },
                ],
              })
            : series?.areaStyle?.color?.colorStops?.push(
                {
                  offset: 0.3,
                  color: seriesColor,
                },
                {
                  offset: 0.6,
                  color: newShade(seriesColor, 70),
                }
              );
        }
      });
    }
    return seriesData;
  };

  const getLineOption = () => {
    const initialLabelLength =
      chartInstance?.longestLabelLength || formatYAxis(maxValue).length;

    let options = {
      tooltip: toolTipConfig
        ? { ...toolTipConfig }
        : {
            show: true,
            trigger: "item",
          },
      xAxis: {
        type: "category",
        data: xAxisData,
        name: xField,
        nameLocation: "middle",
        nameGap: xnamegap,
        boundaryGap: false,
        axisLabel: XaxisLabelFormat ? XaxisLabelFormat : {},
        ...xAxisConfig,
      },
      yAxis: {
        type: "value",
        nameLocation: "middle",
        show: true,
        name: yField,
        ...getYAxisIndex(),
        nameGap: nameGapYAxis(initialLabelLength), // Dynamically assigned value
        axisLabel: YaxisLableConfig
          ? { ...YaxisLableConfig }
          : {
              formatter: (value) => formatYAxis(value), // <== This is the main part!
            },
        // ...(interval !== undefined && { interval }),
        ...yAxisConfig,
      },
      grid: {
        top: spaceTop, // Adjust the top margin as needed
        left: spaceLeft,
        right: spaceRight,
        bottom: spaceBottom,
        containLabel: true,
      },
      legend: {
        data: legend,
        icon: "circle",
        show: showlegend,
        type: "scroll",
        orient: "horizontal",
        left: legendPosition ? legendPosition : "left",
        ...legendCustomFormatter,
      },
      series: getSeriesData(),
    };
    if (isScroll) {
      options.dataZoom = [
        {
          type: "slider",
          start: isScrollStartLimit,
          end: isScrollEndLimit,
          height: 15,
          bottom: scrollBarBottomAlign,
          showDetail: false,
          handleStyle: {
            color: color[0],
          },
          dataBackground: {
            lineStyle: {
              color: color[0], // Set the data background color
            },
            areaStyle: {
              color: "rgba(255, 0, 0, 0.1)", // Set the data background area color
            },
          },
        },
        {
          type: "inside", // Inside zoom
          start: 0,
          end: 30,
        },
      ];
    }
    return options;
  };

  // chart data select
  const handleChartClick = (params) => {
    if (onChartClick) {
      onChartClick(params);
    }
  };

  let chartInstance = null;

  // Legend select event handler
  const handleLegendSelectChanged = (params) => {
    const selectedValues = Object.values(params?.selected);
    const allFalse = selectedValues?.every((value) => value === false);

    if (!chartInstance) {
      return;
    }

    // Get the current Y-axis model after legend toggling
    const yAxisModel = chartInstance.getModel().getComponent("yAxis");
    if (!yAxisModel) {
      return;
    }

    const axis = yAxisModel.axis;
    const tickValues = axis.scale.getTicks();

    // Get the currently rendered labels
    const renderedYAxisLabels = tickValues.map((tick) => {
      const numValue = Number(tick.value);
      return isNaN(numValue) ? "0" : formatYAxis(numValue);
    });

    // Find the longest rendered label length
    const longestLabelLength = Math.max(
      ...renderedYAxisLabels.map((label) => label.length),
      2 // Minimum length
    );

    if (allFalse) {
      const YFilledHide = {
        yAxis: {
          type: "value",
          nameLocation: "middle",
          name: yField,
          show: false,
          nameGap: nameGapYAxis(longestLabelLength),
          axisLabel: YaxisLableConfig
            ? { ...YaxisLableConfig }
            : {
                formatter: (value) => formatYAxis(value),
              },
          ...yAxisConfig,
        },
      };
      chartInstance.setOption(YFilledHide);
    } else {
      const YFilledShow = {
        yAxis: {
          type: "value",
          nameLocation: "middle",
          show: true,
          name: yField,
          nameGap: nameGapYAxis(longestLabelLength),
          axisLabel: YaxisLableConfig
            ? { ...YaxisLableConfig }
            : {
                formatter: (value) => formatYAxis(value),
              },
          ...getYAxisIndex(),
          ...yAxisConfig,
        },
      };
      chartInstance.setOption(YFilledShow);
    }
  };
  const onEvents = {
    click: handleChartClick, // chart click event
    legendselectchanged: handleLegendSelectChanged, // legend click event
    datazoom: recalcNameGap, // scroll/zoom event
  };
  return (
    <div style={{ height: chartheight ? chartheight : null }}>
      <ReactECharts
        option={getLineOption()}
        onEvents={events ? onEvents : {}}
        onChartReady={handleChartReady}
      />
    </div>
  );
};

function areEqual(prevProps, nextProps) {
  if(_.isEqual(prevProps.showLegend, nextProps.showLegend) &&
  _.isEqual(prevProps.events, nextProps.events) &&
  _.isEqual(prevProps.isScroll, nextProps.isScroll) &&
  _.isEqual(prevProps.countTitle, nextProps.countTitle) &&
  _.isEqual(prevProps.spaceLeft, nextProps.spaceLeft) &&
  _.isEqual(prevProps.spaceRight, nextProps.spaceRight) &&
  _.isEqual(prevProps.spaceTop, nextProps.spaceTop) &&
  _.isEqual(prevProps.symbolSize, nextProps.symbolSize) &&
  _.isEqual(prevProps.YaxisLableConfig, nextProps.YaxisLableConfig) &&
  _.isEqual(prevProps.legendCustomFormatter, nextProps.legendCustomFormatter)) {
      return true;
  } else {
      return false;
  }
}

LineChart.propTypes = {
  areaChart: PropTypes.bool,
  chartData: PropTypes.array,
  chartheight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  CloudCostSummaryChart: PropTypes.bool,
  color: PropTypes.array,
  data: PropTypes.array,
   fullData: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.object]),
  events: PropTypes.object,
  isScroll: PropTypes.bool,
  isScrollEndLimit: PropTypes.number,
  isScrollStartLimit: PropTypes.number,
  legend: PropTypes.array,
  LinechartNonmultiple: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  maxDigityAxisSpace: PropTypes.number,
  onChartClick: PropTypes.func,
  otherChartStyle: PropTypes.object,
  outerIndex: PropTypes.any,
  scrollBarBottomAlign: PropTypes.number,
  symbolSize: PropTypes.number,
  shadeAreaDeep: PropTypes.bool,
  showlegend: PropTypes.bool,
  sirartrend: PropTypes.bool,
  smooth: PropTypes.bool,
  isNoGradient: PropTypes.bool,
  spaceBottom: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  spaceLeft: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  spaceRight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  spaceTop: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  symboleSize: PropTypes.number,
  toolTipConfig: PropTypes.object,
  xAxisConfig: PropTypes.object,
  XaxisLabelFormat: PropTypes.object,
  xAxisData: PropTypes.array,
  xField: PropTypes.string,
  legendPosition: PropTypes.string,
  xnamegap: PropTypes.number,
  yAxisConfig: PropTypes.object,
  yAxisSpace: PropTypes.number,
  customizeLine: PropTypes.object,
  yField: PropTypes.string,
  ynamegap: PropTypes.number,  
  YaxisLableConfig: PropTypes.object,  
  legendCustomFormatter: PropTypes.object,
  customSeries: PropTypes.any,
};


export default React.memo (LineChart, areEqual);


